import { useQueryClient } from '@tanstack/react-query'

interface IProps {
    reactQueryKeys?: string[]
    pattern?: string // For pattern-based matching
    mode?: 'exact' | 'contains' | 'startsWith' | 'endsWith'
}

const useQueryClientCallback = ({
    reactQueryKeys = [],
    pattern,
    mode = 'exact',
}: IProps = {}) => {
    const queryClient = useQueryClient()

    const refetch = () => {
        if (pattern) {
            // Pattern-based refetch
            const allQueries = queryClient.getQueryCache().getAll()

            const matchingQueries = allQueries.filter((query) => {
                const queryKey = query.queryKey.join('-').toLowerCase()
                const searchPattern = pattern.toLowerCase()

                switch (mode) {
                    case 'contains':
                        return queryKey.includes(searchPattern)
                    case 'startsWith':
                        return queryKey.startsWith(searchPattern)
                    case 'endsWith':
                        return queryKey.endsWith(searchPattern)
                    case 'exact':
                    default:
                        return queryKey === searchPattern
                }
            })

            matchingQueries.forEach((query) => {
                queryClient.refetchQueries({ queryKey: query.queryKey })
            })

            console.log(
                `Refetched ${matchingQueries.length} queries matching pattern: "${pattern}"`
            )
        } else {
            // Exact key-based refetch (legacy behavior)
            reactQueryKeys.forEach((key) => {
                queryClient.refetchQueries({ queryKey: [key] })
            })
        }
    }

    const invalidate = () => {
        if (pattern) {
            // Pattern-based invalidate
            const allQueries = queryClient.getQueryCache().getAll()

            const matchingQueries = allQueries.filter((query) => {
                const queryKey = query.queryKey.join('-').toLowerCase()
                const searchPattern = pattern.toLowerCase()

                switch (mode) {
                    case 'contains':
                        return queryKey.includes(searchPattern)
                    case 'startsWith':
                        return queryKey.startsWith(searchPattern)
                    case 'endsWith':
                        return queryKey.endsWith(searchPattern)
                    case 'exact':
                    default:
                        return queryKey === searchPattern
                }
            })

            matchingQueries.forEach((query) => {
                queryClient.invalidateQueries({ queryKey: query.queryKey })
            })

            console.log(
                `Invalidated ${matchingQueries.length} queries matching pattern: "${pattern}"`
            )
        } else {
            // Exact key-based invalidate (legacy behavior)
            reactQueryKeys.forEach((key) => {
                queryClient.invalidateQueries({ queryKey: [key] })
            })
        }
    }

    const remove = () => {
        if (pattern) {
            // Pattern-based remove
            const allQueries = queryClient.getQueryCache().getAll()

            const matchingQueries = allQueries.filter((query) => {
                const queryKey = query.queryKey.join('-').toLowerCase()
                const searchPattern = pattern.toLowerCase()

                switch (mode) {
                    case 'contains':
                        return queryKey.includes(searchPattern)
                    case 'startsWith':
                        return queryKey.startsWith(searchPattern)
                    case 'endsWith':
                        return queryKey.endsWith(searchPattern)
                    case 'exact':
                    default:
                        return queryKey === searchPattern
                }
            })

            matchingQueries.forEach((query) => {
                queryClient.removeQueries({ queryKey: query.queryKey })
            })

            console.log(
                `Removed ${matchingQueries.length} queries matching pattern: "${pattern}"`
            )
        } else {
            // Exact key-based remove (legacy behavior)
            reactQueryKeys.forEach((key) => {
                queryClient.removeQueries({ queryKey: [key] })
            })
        }
    }

    return { refetch, invalidate, remove }
}

export default useQueryClientCallback
